import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Constants } from "@/integrations/supabase/types";

const ALL_ROLES = Constants.public.Enums.app_role;

// Currently exposed: academic only (per product scope).
// Add more entries here later to manage other scanners from the same UI.
const QR_TYPES = [
  {
    value: "academic",
    label: "Academic Session Scanner",
    description:
      "Roles allowed to operate the academic session scanner even when no QR visibility rule is configured.",
  },
] as const;

const formatRole = (r: string) =>
  r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function QrBypassRolesManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["qr-bypass-roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("qr_bypass_roles")
        .select("id, qr_type, role");
      if (error) throw error;
      return data || [];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({
      qr_type,
      role,
      enabled,
    }: {
      qr_type: string;
      role: string;
      enabled: boolean;
    }) => {
      if (enabled) {
        const { error } = await supabase
          .from("qr_bypass_roles")
          .insert({ qr_type, role: role as any, created_by: user?.id ?? null });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("qr_bypass_roles")
          .delete()
          .eq("qr_type", qr_type)
          .eq("role", role as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qr-bypass-roles"] });
      toast.success("Bypass roles updated");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update"),
    onSettled: () => setPending(null),
  });

  const isEnabled = (qr_type: string, role: string) =>
    rows.some((r: any) => r.qr_type === qr_type && r.role === role);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <ShieldCheck className="h-4 w-4 text-primary" />
          QR Visibility Bypass Roles
        </CardTitle>
        <CardDescription>
          Choose which roles can start scanning even when no QR visibility rule is active.
          Superadmin is always allowed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          QR_TYPES.map((qt) => (
            <div key={qt.value} className="space-y-3">
              <div>
                <h3 className="font-semibold text-sm">{qt.label}</h3>
                <p className="text-xs text-muted-foreground">{qt.description}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {ALL_ROLES.map((role) => {
                  const enabled = isEnabled(qt.value, role);
                  const isSuper = role === "superadmin";
                  const key = `${qt.value}:${role}`;
                  return (
                    <label
                      key={key}
                      className={`flex items-center gap-2 rounded-md border p-2 text-sm cursor-pointer hover:bg-accent/40 ${
                        enabled ? "border-primary/40 bg-primary/5" : "border-border"
                      } ${isSuper ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      <Checkbox
                        checked={enabled || isSuper}
                        disabled={isSuper || pending === key}
                        onCheckedChange={(v) => {
                          if (isSuper) return;
                          setPending(key);
                          toggleMutation.mutate({
                            qr_type: qt.value,
                            role,
                            enabled: !!v,
                          });
                        }}
                      />
                      <span className="flex-1">{formatRole(role)}</span>
                      {isSuper && (
                        <Badge variant="secondary" className="text-[10px]">
                          Always
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
